import React from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, Stack, Divider
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Star as StarIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';

const planColors = {
  FREE: '#64748B',
  STARTER: '#3B82F6',
  PRO: '#8B5CF6',
  BUSINESS: '#F59E0B',
  ENTERPRISE: '#1E2783',
};

function FeatureRow({ label, included }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
      {included ? (
        <CheckIcon sx={{ fontSize: 18, color: '#10B981' }} />
      ) : (
        <CloseIcon sx={{ fontSize: 18, color: '#CBD5E1' }} />
      )}
      <Typography variant="body2" color={included ? 'text.primary' : 'text.disabled'}>
        {label}
      </Typography>
    </Box>
  );
}

function PlanCard({ plan, planActual, periodo, onSelect, popular }) {
  const color = planColors[plan.codigo_plan] || '#1E2783';
  const esActual = planActual === plan.codigo_plan;
  const precio = periodo === 'ANUAL'
    ? (Number(plan.precio_anual_usd) / 12).toFixed(2)
    : Number(plan.precio_mensual_usd).toFixed(2);

  return (
    <Card
      sx={{
        flex: '1 1 220px',
        maxWidth: 280,
        borderRadius: 3,
        border: popular ? `2px solid ${color}` : '1px solid #E2E8F0',
        position: 'relative',
        transition: 'transform 0.2s',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
      }}
    >
      {popular && (
        <Chip
          icon={<StarIcon sx={{ fontSize: 14 }} />}
          label="Popular"
          size="small"
          sx={{
            position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
            bgcolor: color, color: '#FFF', fontWeight: 700,
          }}
        />
      )}

      <CardContent sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" fontWeight={800} color={color} gutterBottom>
          {plan.nombre}
        </Typography>

        {plan.codigo_plan === 'ENTERPRISE' ? (
          <Typography variant="h5" fontWeight={800} color="#1E2783" sx={{ my: 2 }}>
            Personalizado
          </Typography>
        ) : plan.codigo_plan === 'FREE' ? (
          <Typography variant="h4" fontWeight={900} color={color} sx={{ my: 2 }}>
            Gratis
          </Typography>
        ) : (
          <Box sx={{ my: 2 }}>
            <Typography variant="h4" fontWeight={900} color={color} component="span">
              ${precio}
            </Typography>
            <Typography variant="body2" color="text.secondary" component="span">
              /mes
            </Typography>
            {periodo === 'ANUAL' && (
              <Typography variant="caption" display="block" color="text.secondary">
                ${Number(plan.precio_anual_usd).toFixed(0)} facturado anualmente
              </Typography>
            )}
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
          {plan.descripcion}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ textAlign: 'left' }}>
          <FeatureRow label={plan.max_tecnicos ? `Hasta ${plan.max_tecnicos} técnicos` : 'Técnicos ilimitados'} included />
          <FeatureRow label={plan.max_locales ? `${plan.max_locales} local${plan.max_locales > 1 ? 'es' : ''}` : 'Locales ilimitados'} included />
          <FeatureRow label={plan.max_servicios_mes ? `${plan.max_servicios_mes} servicios` : 'Servicios ilimitados'} included />
          <FeatureRow label="GPS en tiempo real" included={plan.incluye_gps} />
          <FeatureRow label="Módulo Delivery" included={plan.incluye_delivery} />
          <FeatureRow label="Acceso API" included={plan.incluye_api} />
        </Box>

        <Box sx={{ mt: 3 }}>
          {esActual ? (
            <Button fullWidth variant="outlined" disabled sx={{ borderRadius: 2 }}>
              Plan actual
            </Button>
          ) : plan.codigo_plan === 'FREE' ? (
            <Button fullWidth variant="outlined" disabled sx={{ borderRadius: 2 }}>
              Solo al registrarse
            </Button>
          ) : plan.codigo_plan === 'ENTERPRISE' ? (
            <Button
              fullWidth variant="contained"
              startIcon={<WhatsAppIcon />}
              href="https://wa.me/51993540571?text=Hola, me interesa el plan Enterprise de TRACKEA"
              target="_blank"
              sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' }, borderRadius: 2, fontWeight: 700 }}
            >
              Contactar
            </Button>
          ) : (
            <Button
              fullWidth variant="contained"
              onClick={() => onSelect(plan.id_plan, periodo)}
              sx={{
                bgcolor: color, '&:hover': { bgcolor: color, filter: 'brightness(0.9)' },
                borderRadius: 2, fontWeight: 700,
              }}
            >
              Elegir plan
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function PlanesComparativa({ planes, planActual, onSelectPlan }) {
  const [periodo, setPeriodo] = React.useState('MENSUAL');

  if (!planes || planes.length === 0) return null;

  return (
    <Box>
      {/* Toggle periodo */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4, gap: 1 }}>
        <Button
          variant={periodo === 'MENSUAL' ? 'contained' : 'outlined'}
          onClick={() => setPeriodo('MENSUAL')}
          sx={{ borderRadius: 2, px: 3, bgcolor: periodo === 'MENSUAL' ? '#1E2783' : undefined }}
        >
          Mensual
        </Button>
        <Button
          variant={periodo === 'ANUAL' ? 'contained' : 'outlined'}
          onClick={() => setPeriodo('ANUAL')}
          sx={{ borderRadius: 2, px: 3, bgcolor: periodo === 'ANUAL' ? '#1E2783' : undefined }}
        >
          Anual <Chip label="Ahorra 20%" size="small" sx={{ ml: 1, bgcolor: '#10B981', color: '#FFF', fontSize: 10 }} />
        </Button>
      </Box>

      {/* Grid de planes */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
        {planes
          .filter(p => p.codigo_plan !== 'FREE')
          .map(plan => (
            <PlanCard
              key={plan.id_plan}
              plan={plan}
              planActual={planActual}
              periodo={periodo}
              onSelect={onSelectPlan}
              popular={plan.codigo_plan === 'PRO'}
            />
          ))}
      </Box>
    </Box>
  );
}
